import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
    Container, Typography, CircularProgress, Box, Stack,
    Alert, Chip, Grid, Button,
} from '@mui/material';
import {
    TrendingUp, TrendingDown, ShowChart, Warning,
} from '@mui/icons-material';
import { Surface, StatCard } from '../components/primitives';

// Compute the option's position state purely from its fields. Mirrors the
// legacy server-side logic — running it in the browser means options that
// expire today never show stale data (the precomputed summary count can lag
// by up to a day, which is acceptable for the dashboard header).
function computePositionStatus(opt) {
    if (opt.transaction !== 'purchase') return 'sold';
    if (opt.option_resold === true) return 'closed';
    const exp = opt.option_exp_date;
    if (!exp) return 'unknown';
    const now = new Date();
    const expDate = new Date(`${exp}T00:00:00`);
    if (expDate > now) return 'open';

    const strike = opt.strike_price ?? 0;
    const expPrice = opt.stock_price_at_expiry ?? opt.stock_price_1yr ?? opt.stock_price;
    if (!expPrice || !strike) return 'expired';
    if (opt.option_type === 'call') return expPrice > strike ? 'expired_itm' : 'expired_otm';
    if (opt.option_type === 'put')  return expPrice < strike ? 'expired_itm' : 'expired_otm';
    return 'expired';
}

const STATUS_CONFIG = {
    open:        { label: 'Open',             color: 'primary.light',  bg: 'rgba(99,102,241,0.1)',  border: 'rgba(99,102,241,0.25)' },
    closed:      { label: 'Closed (Resold)',  color: 'text.secondary', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.25)' },
    expired_itm: { label: 'Likely Exercised', color: 'success.main',   bg: 'rgba(34,197,94,0.1)',   border: 'rgba(34,197,94,0.25)' },
    expired_otm: { label: 'Expired Worthless', color: 'error.main',    bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.25)' },
    expired:     { label: 'Expired',          color: 'text.muted',     bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.25)' },
    sold:        { label: 'Contract Sold',    color: 'text.muted',     bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.25)' },
    unknown:     { label: 'Unknown',          color: 'text.muted',     bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.25)' },
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr + 'T00:00:00');
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
};

const formatValue = (low, high) => {
    if (!low && !high) return '—';
    const fmt = (n) => {
        if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
        if (n >= 1000) return '$' + (n / 1000).toFixed(0) + 'K';
        return '$' + n;
    };
    return fmt(low) + '–' + fmt(high);
};

const translateOption = (opt) => {
    const ticker = opt.ticker;
    const type = opt.option_type;
    const strike = opt.strike_price;
    const isBuy = opt.transaction === 'purchase';
    const price = opt.stock_price;

    let meaning = '';
    if (type === 'call' && isBuy) meaning = 'Bullish bet — profits if ' + ticker + ' rises above $' + strike;
    else if (type === 'call' && !isBuy) meaning = 'Sold call — collected premium, risk if ' + ticker + ' rises above $' + strike;
    else if (type === 'put' && isBuy) meaning = 'Bearish bet — profits if ' + ticker + ' falls below $' + strike;
    else if (type === 'put' && !isBuy) meaning = 'Sold put — collected premium, risk if ' + ticker + ' falls below $' + strike;

    if (price && strike) {
        const itm = (type === 'call' && price > strike) || (type === 'put' && price < strike);
        meaning += itm ? ' (was ITM at purchase)' : ' (was OTM at purchase)';
    }
    return meaning;
};

const OptionRow = ({ opt }) => {
    const status = STATUS_CONFIG[opt.position_status] || STATUS_CONFIG.unknown;
    const isBuy = opt.transaction === 'purchase';
    const val = formatValue(opt.asset_value_low, opt.asset_value_high);

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
        >
            <Surface padding={0} sx={{ mb: 1.5, overflow: 'hidden' }}>
                <Box sx={{ p: 2.5 }}>
                    {/* Top row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{
                                color: 'text.primary', fontWeight: 600,
                                '&:hover': { color: 'primary.light' },
                            }}
                                component={opt.member_id ? Link : 'span'}
                                to={opt.member_id ? '/congressmember/' + opt.member_id : undefined}
                                style={{ textDecoration: 'none' }}
                            >
                                {opt.first_name} {opt.last_name}
                            </Typography>
                            <Chip
                                label={isBuy ? 'BUY' : 'SELL'}
                                size="small"
                                sx={{
                                    height: 20, fontSize: '0.65rem', fontWeight: 700,
                                    bgcolor: isBuy ? 'rgba(99,102,241,0.15)' : 'rgba(148,163,184,0.15)',
                                    color: isBuy ? 'custom.direction.buyFg' : 'custom.direction.sellFg',
                                    border: isBuy
                                        ? '1px solid rgba(99,102,241,0.3)'
                                        : '1px solid rgba(148,163,184,0.3)',
                                }}
                            />
                            <Typography
                                component={Link}
                                to={'/issuers/' + encodeURIComponent(opt.ticker)}
                                sx={{
                                    fontFamily: t => t.typography.fontFamilyMono,
                                    fontWeight: 700, fontSize: '1rem', color: 'text.primary',
                                    textDecoration: 'none',
                                    '&:hover': { color: 'primary.light' },
                                }}
                            >
                                {opt.ticker}
                            </Typography>
                            <Chip
                                label={opt.option_type === 'call' ? 'CALL' : 'PUT'}
                                size="small"
                                sx={{
                                    height: 20, fontSize: '0.65rem', fontWeight: 700,
                                    bgcolor: opt.option_type === 'call'
                                        ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                                    color: opt.option_type === 'call'
                                        ? 'success.main' : 'error.main',
                                    border: opt.option_type === 'call'
                                        ? '1px solid rgba(34,197,94,0.25)'
                                        : '1px solid rgba(239,68,68,0.25)',
                                }}
                            />
                        </Box>
                        {isBuy && (
                            <Chip
                                label={status.label}
                                size="small"
                                sx={{
                                    height: 22, fontSize: '0.7rem', fontWeight: 600,
                                    bgcolor: status.bg,
                                    color: status.color,
                                    border: '1px solid ' + status.border,
                                }}
                            />
                        )}
                    </Box>

                    {/* Details */}
                    <Stack direction="row" spacing={3} sx={{ mb: 1, flexWrap: 'wrap' }}>
                        <Box>
                            <Typography variant="overline" sx={{ color: 'text.muted', display: 'block', lineHeight: 1 }}>
                                Strike
                            </Typography>
                            <Typography variant="body2" sx={{
                                fontFamily: t => t.typography.fontFamilyMono,
                                color: 'text.primary', fontWeight: 600,
                            }}>
                                ${opt.strike_price}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="overline" sx={{ color: 'text.muted', display: 'block', lineHeight: 1 }}>
                                Expires
                            </Typography>
                            <Typography variant="body2" sx={{
                                fontFamily: t => t.typography.fontFamilyMono,
                                color: 'text.primary',
                            }}>
                                {formatDate(opt.option_exp_date)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="overline" sx={{ color: 'text.muted', display: 'block', lineHeight: 1 }}>
                                Value
                            </Typography>
                            <Typography variant="body2" sx={{
                                fontFamily: t => t.typography.fontFamilyMono,
                                color: 'text.primary',
                            }}>
                                {val}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="overline" sx={{ color: 'text.muted', display: 'block', lineHeight: 1 }}>
                                Stock at Trade
                            </Typography>
                            <Typography variant="body2" sx={{
                                fontFamily: t => t.typography.fontFamilyMono,
                                color: 'text.primary',
                            }}>
                                ${opt.stock_price?.toFixed(2) || '—'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="overline" sx={{ color: 'text.muted', display: 'block', lineHeight: 1 }}>
                                Traded
                            </Typography>
                            <Typography variant="body2" sx={{
                                fontFamily: t => t.typography.fontFamilyMono,
                                color: 'text.muted',
                            }}>
                                {formatDate(opt.transaction_date)}
                            </Typography>
                        </Box>
                        {opt.notification_date && opt.notification_date !== opt.transaction_date && (
                            <Box>
                                <Typography variant="overline" sx={{ color: 'text.muted', display: 'block', lineHeight: 1 }}>
                                    Disclosed
                                </Typography>
                                <Typography variant="body2" sx={{
                                    fontFamily: t => t.typography.fontFamilyMono,
                                    color: 'text.muted',
                                }}>
                                    {formatDate(opt.notification_date)}
                                </Typography>
                            </Box>
                        )}
                        {opt.option_resold_date && (
                            <Box>
                                <Typography variant="overline" sx={{ color: 'text.muted', display: 'block', lineHeight: 1 }}>
                                    Resold
                                </Typography>
                                <Typography variant="body2" sx={{
                                    fontFamily: t => t.typography.fontFamilyMono,
                                    color: 'text.muted',
                                }}>
                                    {formatDate(opt.option_resold_date)}
                                </Typography>
                            </Box>
                        )}
                    </Stack>

                    {/* Plain English translation */}
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        {translateOption(opt)}
                    </Typography>
                </Box>
            </Surface>
        </motion.div>
    );
};

function OptionsActivity() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('open');
    const [traderFilter, setTraderFilter] = useState(null);
    const [visibleCount, setVisibleCount] = useState(30);

    useEffect(() => {
        let cancelled = false;
        const fetchOptions = async () => {
            try {
                const session = await fetchAuthSession();
                const idToken = session?.tokens?.idToken?.toString();
                if (!idToken) {
                    setError(new Error('Not authenticated.'));
                    return;
                }
                const headers = { Authorization: 'Bearer ' + idToken };

                // Both endpoints are Pro-only and Cognito-authorized at the
                // API Gateway. The Lambda also re-checks is_pro_user and
                // returns 403 for non-Pro callers. position_status is
                // computed below in the browser so options expiring today
                // are never stale.
                const [summaryRes, tradesRes] = await Promise.all([
                    fetch('https://api.capitolgains.io/v1/options/summary', { headers }),
                    fetch('https://api.capitolgains.io/v1/options/trades?limit=200', { headers }),
                ]);

                if (summaryRes.status === 403 || tradesRes.status === 403) {
                    if (!cancelled) setError({ code: 'pro_required' });
                    return;
                }
                if (!summaryRes.ok || !tradesRes.ok) {
                    throw new Error('Failed to load options data');
                }

                const summaryEnvelope = await summaryRes.json();
                const tradesEnvelope = await tradesRes.json();

                const options = (tradesEnvelope.data || []).map((o) => ({
                    ...o,
                    position_status: computePositionStatus(o),
                }));

                if (!cancelled) {
                    setData({
                        summary: summaryEnvelope.data,
                        options,
                    });
                }
            } catch (err) {
                if (!cancelled) setError(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchOptions();
        return () => { cancelled = true; };
    }, []);

    const filtered = useMemo(() => {
        if (!data?.options) return [];
        let opts = data.options;
        if (filter === 'open') opts = opts.filter(o => o.position_status === 'open');
        if (filter === 'purchases') opts = opts.filter(o => o.transaction === 'purchase');
        if (filter === 'calls') opts = opts.filter(o => o.option_type === 'call');
        if (filter === 'puts') opts = opts.filter(o => o.option_type === 'put');
        if (traderFilter) opts = opts.filter(o =>
            ((o.first_name || '') + ' ' + (o.last_name || '')).trim() === traderFilter
        );
        return opts;
    }, [data, filter, traderFilter]);

    if (loading) {
        return (
            <Container sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error?.code === 'pro_required') {
        return (
            <Container sx={{ pt: 6 }}>
                <Alert
                    severity="info"
                    action={
                        <Button component={Link} to="/pricing" variant="contained" size="small">
                            Upgrade
                        </Button>
                    }
                >
                    Options activity is a Capitol Gains Pro feature.
                </Alert>
            </Container>
        );
    }

    if (error) {
        return (
            <Container sx={{ pt: 6 }}>
                <Alert severity="error">Error: {error.message}</Alert>
            </Container>
        );
    }

    if (!data) return null;

    const { summary } = data;

    return (
        <Container maxWidth="lg" sx={{ pt: 6, pb: 8 }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h1" sx={{ color: 'text.primary', mb: 0.5 }}>
                        Options Activity
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 640 }}>
                        Congressional options trades — the highest-leverage bets in Congress.
                        Options provide 10-50x leverage on stock positions, making them the most
                        aggressive (and potentially most profitable) trades members make.
                    </Typography>
                </Box>

                {/* Hero stats */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            icon={ShowChart}
                            label="Total Trades"
                            value={summary.total_trades}
                            mono
                            tooltip="Total options transactions disclosed by Congress members"
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            icon={TrendingUp}
                            label="Open Positions"
                            value={summary.open_positions}
                            mono
                            tone={summary.open_positions > 0 ? 'positive' : 'default'}
                            tooltip="Options purchased that haven't expired or been resold — active bets right now"
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            icon={TrendingUp}
                            label="Calls (Bullish)"
                            value={summary.calls}
                            mono
                            tooltip="Call options — bullish bets that profit when the stock price rises above the strike"
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            icon={TrendingDown}
                            label="Puts (Bearish)"
                            value={summary.puts}
                            mono
                            tooltip="Put options — bearish bets that profit when the stock price falls below the strike"
                        />
                    </Grid>
                </Grid>

                {/* Disclaimer */}
                <Surface variant="subtle" padding={2} sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Warning sx={{ color: 'text.muted', fontSize: 18, mt: '2px' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                            Options data from mandatory STOCK Act filings. Position status inferred from
                            expiration dates and stock prices. "Likely Exercised" = option was in the money at expiration.
                        </Typography>
                    </Stack>
                </Surface>

                {/* Filters */}
                <Stack direction="row" spacing={0.75} sx={{ mb: 3, flexWrap: 'wrap' }}>
                    {[
                        { key: 'all', label: 'All Trades' },
                        { key: 'open', label: 'Open Positions' },
                        { key: 'purchases', label: 'Purchases Only' },
                        { key: 'calls', label: 'Calls' },
                        { key: 'puts', label: 'Puts' },
                    ].map(f => (
                        <Chip
                            key={f.key}
                            label={f.label}
                            onClick={() => { setFilter(f.key); setVisibleCount(30); }}
                            sx={{
                                bgcolor: filter === f.key ? 'custom.surface.elevated' : 'transparent',
                                color: filter === f.key ? 'text.primary' : 'text.secondary',
                                border: t => '1px solid ' + (filter === f.key
                                    ? t.palette.custom.border.default
                                    : t.palette.custom.border.subtle),
                                fontWeight: filter === f.key ? 600 : 400,
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'custom.surface.elevated' },
                            }}
                        />
                    ))}
                </Stack>

                {/* Options list */}
                {filtered.length === 0 ? (
                    <Surface padding={4} sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'text.muted' }}>
                            No options trades match this filter.
                        </Typography>
                    </Surface>
                ) : (
                    <>
                        <Typography variant="caption" sx={{ color: 'text.muted', mb: 1.5, display: 'block' }}>
                            Showing {Math.min(visibleCount, filtered.length)} of {filtered.length} trades
                        </Typography>
                        {filtered.slice(0, visibleCount).map((opt, i) => (
                            <OptionRow key={opt.transaction_date + '-' + opt.ticker + '-' + i} opt={opt} />
                        ))}
                        {visibleCount < filtered.length && (
                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Chip
                                    label={'Show more (' + (filtered.length - visibleCount) + ' remaining)'}
                                    onClick={() => setVisibleCount(v => v + 30)}
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: 'custom.surface.elevated',
                                        color: 'text.primary',
                                        border: t => '1px solid ' + t.palette.custom.border.default,
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}

                {/* Top options traders */}
                {summary.top_traders?.length > 0 && (
                    <Surface sx={{ mt: 4 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h3" sx={{ color: 'text.primary' }}>
                                Most Active Options Traders
                            </Typography>
                            {traderFilter && (
                                <Chip
                                    label={'Clear filter: ' + traderFilter}
                                    onDelete={() => setTraderFilter(null)}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(99,102,241,0.15)',
                                        color: 'primary.light',
                                        border: '1px solid rgba(99,102,241,0.3)',
                                    }}
                                />
                            )}
                        </Stack>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {summary.top_traders.map((t, i) => (
                                <Chip
                                    key={i}
                                    label={t.name + '  \u00d7' + t.trade_count}
                                    size="small"
                                    onClick={() => {
                                        setTraderFilter(traderFilter === t.name ? null : t.name);
                                        setFilter('all');
                                        setVisibleCount(30);
                                    }}
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: traderFilter === t.name ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.04)',
                                        color: traderFilter === t.name ? 'primary.light' : 'text.primary',
                                        border: th => '1px solid ' + (traderFilter === t.name
                                            ? 'rgba(99,102,241,0.3)'
                                            : th.palette.custom.border.subtle),
                                        fontFamily: th => th.typography.fontFamilyMono,
                                        fontSize: '0.75rem',
                                        '&:hover': { bgcolor: 'rgba(99,102,241,0.1)' },
                                    }}
                                />
                            ))}
                        </Box>
                    </Surface>
                )}
            </motion.div>
        </Container>
    );
}

export default OptionsActivity;
